import { useEffect, useState } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { SidebarSimple, CaretDown } from "@phosphor-icons/react";

/** Settings › Sidebar Menu — tenant-wide choice between collapsible groups and an always-expanded menu. */
export default function SidebarMenuSettings() {
  const { checkAuth } = useAuth();
  const [collapsible, setCollapsible] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/tenant/ui-preferences").then(({ data }) => setCollapsible(data.nav_collapsible_groups !== false)).catch(() => setCollapsible(true));
  }, []);

  const save = async (next) => {
    setSaving(true);
    setCollapsible(next);
    try {
      await API.put("/tenant/ui-preferences", { nav_collapsible_groups: next });
      toast.success(next ? "Sidebar groups now collapse and expand" : "Sidebar groups are now always expanded");
      checkAuth && checkAuth();
    } catch (e) {
      setCollapsible(!next);
      toast.error(e.response?.data?.detail || "Could not save the sidebar preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl" data-testid="sidebar-menu-settings">
      <h3 className="text-base font-semibold font-[Chivo] mb-1">Sidebar menu</h3>
      <p className="text-xs text-muted-foreground mb-4">How the grouped sections (Calendar, Communications, Clients, In-House Team…) behave for everyone in your workspace.</p>
      <label className="flex items-start justify-between gap-4 rounded-lg border border-border p-4 cursor-pointer hover:bg-muted/40 transition-colors">
        <div className="flex items-start gap-3 min-w-0">
          <SidebarSimple size={20} className="mt-0.5 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Collapsible groups</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              On: each group opens and closes with a tap, the group holding the page you're on opens automatically and each person's open/closed choices are remembered on their device.
              Off: every sub-item is always visible under its group heading.
            </p>
          </div>
        </div>
        <Switch checked={collapsible === true} disabled={collapsible === null || saving} onCheckedChange={save} data-testid="sidebar-collapsible-switch" aria-label="Collapsible sidebar groups" />
      </label>
      <div className="mt-4 rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="sidebar-menu-preview">
        <p className="font-medium text-foreground mb-2">Preview</p>
        <div className="space-y-1 w-56">
          <div className="flex items-center gap-2 rounded px-2 py-1 bg-muted"><span className="flex-1">Clients</span>{collapsible !== false && <CaretDown size={12} />}</div>
          <div className="pl-6 space-y-0.5">
            <div className="rounded px-2 py-0.5">Clients</div>
            <div className="rounded px-2 py-0.5">Sub Clients</div>
            <div className="rounded px-2 py-0.5">Properties</div>
            <div className="rounded px-2 py-0.5">Tenant Report Forms</div>
          </div>
          <div className="flex items-center gap-2 rounded px-2 py-1"><span className="flex-1">In-House Team</span>{collapsible !== false && <CaretDown size={12} className="-rotate-90" />}</div>
          {collapsible === false && <div className="pl-6 space-y-0.5"><div className="rounded px-2 py-0.5">Team</div><div className="rounded px-2 py-0.5">Dispatch Board</div><div className="rounded px-2 py-0.5 text-muted-foreground/60">…</div></div>}
        </div>
      </div>
    </div>
  );
}
